/**
 * Note Section Container
 *
 * Layout wrapper for note sections providing consistent width constraints and margins.
 * Used by NoteTitleEditor and NoteSectionItem to ensure uniform layout.
 */

import React from 'react';
import { Box, type SxProps, type Theme } from '@mui/material';

import { LAYOUT_SPACING, SECTION_MAX_WIDTH_WITH_PADDING } from '../../constants/layout';

export interface NoteSectionContainerProps {
  /**
   * Content to display in the container
   */
  children: React.ReactNode;

  /**
   * Maximum width for the content
   * Can be a number, string, or responsive object
   * @default Responsive values from SECTION_MAX_WIDTH_WITH_PADDING
   */
  maxWidth?:
    | number
    | string
    | {
        xs?: number | string;
        sm?: number | string;
        md?: number | string;
        lg?: number | string;
        xl?: number | string;
      };

  /**
   * Horizontal padding (responsive)
   * @default LAYOUT_SPACING.SECTION_HORIZONTAL_PADDING ('min(80px, 5%)')
   */
  horizontalPadding?:
    | {
        xs: string | number;
        sm?: string | number;
        md?: string | number;
        lg?: string | number;
        xl?: string | number;
      }
    | string
    | number;

  /**
   * Additional sx props to merge with container styles
   */
  sx?: SxProps<Theme>;

  /**
   * Optional CSS class name
   */
  className?: string;
}

/**
 * NoteSectionContainer - Layout wrapper for note content
 *
 * Provides consistent width constraints and responsive margins for note sections.
 * Ensures all note content (title, sections) has the same layout boundaries.
 *
 * @example
 * ```tsx
 * <NoteSectionContainer>
 *   <Typography variant="h1">Note Title</Typography>
 * </NoteSectionContainer>
 *
 * <NoteSectionContainer sx={{ mb: 4 }}>
 *   <NoteSectionItem section={section} />
 * </NoteSectionContainer>
 * ```
 */
export const NoteSectionContainer: React.FC<NoteSectionContainerProps> = ({
  children,
  maxWidth,
  horizontalPadding = LAYOUT_SPACING.SECTION_HORIZONTAL_PADDING,
  sx,
  className,
}) => {
  // Default to responsive maxWidth that includes padding
  // This ensures content stays at 800px while container expands
  const defaultMaxWidth:
    | number
    | string
    | { xs?: number | string; sm?: number | string; md?: number | string } = maxWidth ?? {
    xs: SECTION_MAX_WIDTH_WITH_PADDING.xs, // 832px
    sm: SECTION_MAX_WIDTH_WITH_PADDING.sm, // 848px
    md: SECTION_MAX_WIDTH_WITH_PADDING.md, // 864px
  };

  return (
    <Box
      className={`note-section-container ${className ?? ''}`.trim()}
      sx={
        [
          {
            width: '100%',
            maxWidth: defaultMaxWidth,
            px: horizontalPadding,
            mx: LAYOUT_SPACING.SECTION_HORIZONTAL_MARGIN,
          },
          ...(sx ? [sx] : []),
        ] as SxProps<Theme>
      }
    >
      {children}
    </Box>
  );
};
